import { Star, Quote } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useRef } from "react";
import { REVIEW_COUNT } from "@/config/reviews";


const reviews = [
  {
    name: "Joey P.",
    text: "The transformation of our backyard was beyond our expectations. The paver patio they installed has become the centerpiece of all our family gatherings. Beautiful craftsmanship!",
    rating: 5,
  },
  {
    name: "Tommy F.",
    text: "After getting several estimates, we chose Masonry Pro and couldn't be happier. They built a stunning stone retaining wall that solved our drainage issues while adding incredible curb appeal.",
    rating: 5,
  },
  {
    name: "Holly G.",
    text: "Professional from start to finish. They replaced our crumbling concrete driveway with a beautiful stamped concrete design. Two years later, it still looks brand new!",
    rating: 5,
  },
  {
    name: "Anna S.",
    text: "Our outdoor kitchen project was complex, but this team handled every detail perfectly. From the stone countertops to the built-in grill station, everything exceeded our expectations.",
    rating: 5,
  },
  {
    name: "A.S.",
    text: "We had an old brick walkway that was uneven and dangerous. They removed it and installed beautiful bluestone pavers. Now it's both safe and gorgeous!",
    rating: 5,
  },
  {
    name: "Josh F.",
    text: "The fire pit area they created has become our favorite spot. Quality stonework, perfect placement, and they even added subtle lighting. Worth every penny!",
    rating: 5,
  },
  {
    name: "Blake B.",
    text: "Fixed our leaning retaining wall that another company had botched. They did it right with proper drainage and reinforcement. It's been three years and hasn't moved an inch.",
    rating: 5,
  },
  {
    name: "Roksolana M.",
    text: "Incredible attention to detail on our front steps and stoop. They matched the existing stonework on our 1920s home perfectly. Neighbors keep asking who did the work!",
    rating: 5,
  },
  {
    name: "Craig A.",
    text: "Had them repair tuckpointing on our chimney and it looks better than when the house was built. Fair pricing, clean work, and they finished ahead of schedule.",
    rating: 5,
  },
  {
    name: "Brianna V.",
    text: "The team installed a beautiful stone veneer on our foundation and it completely transformed our home's exterior. So many compliments from neighbors!",
    rating: 5,
  },
];

export function ReviewsSection() {
  const plugin = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: true, stopOnMouseEnter: true })
  );

  return (
    <section className="py-16 bg-card border-y border-border">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-secondary text-secondary" />
              ))}
            </div>
            <span className="text-2xl font-bold text-foreground">5.0</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold mb-2">
            What Our Customers Say
          </h2>
          
          <p className="text-muted-foreground">
            {`${REVIEW_COUNT}+ Google Reviews from North Jersey homeowners`}
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto relative">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[plugin.current]}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {reviews.map((review, index) => (
                <CarouselItem 
                  key={index} 
                  className="pl-4 basis-full md:basis-1/2 lg:basis-1/3"
                >
                  <div className="h-full bg-background rounded-xl p-6 border border-border hover:border-secondary/50 transition-colors duration-300">
                    <Quote className="h-8 w-8 text-secondary/30 mb-4" />
                    
                    <div className="flex mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-secondary text-secondary" />
                      ))}
                    </div>
                    
                    <p className="text-foreground/90 text-sm leading-relaxed mb-6 line-clamp-5">
                      "{review.text}"
                    </p>
                    
                    <div className="mt-auto pt-4 border-t border-border">
                      <p className="font-semibold text-foreground">{review.name}</p>
                      <p className="text-xs text-muted-foreground">Google Review</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-12 bg-background border-border hover:bg-secondary hover:text-secondary-foreground" />
            <CarouselNext className="hidden md:flex -right-12 bg-background border-border hover:bg-secondary hover:text-secondary-foreground" />
          </Carousel>
          
          {/* Navigation dots */}
          <div className="flex justify-center gap-2 mt-6">
            {[...Array(Math.ceil(reviews.length / 3))].map((_, i) => (
              <div 
                key={i} 
                className="w-2 h-2 rounded-full bg-secondary/30"
              />
            ))}
          </div>
          
          <div className="text-center mt-6">
            <a 
              href="https://www.google.com/maps/place/Masonry+Pro+Construction/@40.9077604,-74.2174174,17z/data=!4m18!1m9!3m8!1s0x89c2fdfe4c9fca95:0xdad63374c7f86ff2!2sMasonry+Pro+Construction!8m2!3d40.9077564!4d-74.2148425!9m1!1b1!16s%2Fg%2F1ptz3yl87!3m7!1s0x89c2fdfe4c9fca95:0xdad63374c7f86ff2!8m2!3d40.9077564!4d-74.2148425!9m1!1b1!16s%2Fg%2F1ptz3yl87?utm_source=masonrypro&utm_medium=testimonials_cta&utm_campaign=homepage_trust" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-secondary hover:text-secondary/80 transition-colors font-medium text-sm"
            >
              View All Reviews on Google →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
